import tinycolor from 'tinycolor2';

import {
  TILESET_RASTER_OPACITY,
  TILESET_RASTER_RESAMPLING,
  MAPBOX_SOURCECACHE_LAYER_PREFIX,
} from '../../../constants/map';
import { RGBA_LENGTH } from './constants';
import { translateXY } from './helpers';

/**
 * Returns the color at a given point on a raster layer, as a hex string, or
 * null if the color can not be determined.
 *
 * Adapted from https://github.com/mapbox/mapbox-gl-js/pull/12116 and
 * https://github.com/mapbox/mapbox-gl-js/issues/1404#issuecomment-1599396303
 *
 * WARNING: this function uses internal Mapbox GL JS APIs.
 */
const colorForLayerPoint = (
  map,
  layerId,
  point,
  // Can specify overrides for layer's opacity & resampling, but uses these as defaults:
  {
    originalOpacity = TILESET_RASTER_OPACITY,
    originalResampling = TILESET_RASTER_RESAMPLING,
  } = {},
) => {
  /* eslint-disable no-underscore-dangle */

  try {
    // Pull the layer & ensure it's a raster layer.
    const { painter } = map;
    const layer = painter.style._layers[layerId];
    if (!layer || layer.type !== 'raster') return null;

    // Adjust the raster layer's settings so we can read the actual colors
    layer.paint._values['raster-opacity'] = 1;
    layer.paint._values['raster-resampling'] = 'nearest';

    // Clear the painter so we can just draw this one layer
    painter.context.viewport.set([0, 0, painter.width, painter.height]);
    painter.context.clear({
      color: 'rgba(0,0,0,0)',
      depth: 1,
    });
    painter.clearStencil();

    // Render the layer
    const sourceCacheKey = `${MAPBOX_SOURCECACHE_LAYER_PREFIX}${layer.source}`;
    const sourceCache = painter.style._sourceCaches[sourceCacheKey];
    const coords = sourceCache.getVisibleCoordinates().slice().reverse();
    painter.renderPass = 'translucent';
    painter.renderLayer(painter, sourceCache, layer, coords);

    // Pull the color data we need at the point
    const { gl } = painter.context;
    const pixel = new Uint8Array(RGBA_LENGTH);
    const { x, y } = translateXY(point, gl);
    gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);

    // Return the color as a hex string
    const [r, g, b, a] = pixel;
    return tinycolor({ r, g, b, a }).toHexString();
  } catch (err) {
    console.error(err);
    return null;
  } finally {
    // Reset the raster opacity for the layer
    map.setPaintProperty(layerId, 'raster-opacity', originalOpacity);
    map.setPaintProperty(layerId, 'raster-resampling', originalResampling);

    // Repaint the map so the user doesn't see our render
    map.triggerRepaint();
  }
  /* eslint-enable no-underscore-dangle */
};

export default colorForLayerPoint;
